import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  logout,
  getSalesforceAccessToken,
  fetchAllMasters,
  setImpersonatedEmail,
  clearImpersonatedEmail,
} from "@redux/actions/utility-actions";
import { logImpersonationEvent } from "@redux/actions/impersonation-actions";

export const useImpersonation = (userDetails) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const startImpersonation = async (email) => {
    setLoading(true);
    setError("");

    try {
      const currentEmail = userDetails?.Email?.replace(".invalid", ".cwpmconect");

      // Clear current session state (matches original working implementation)
      await dispatch(logout());
      await dispatch(clearImpersonatedEmail());

      // Get token + user details for impersonated user via proven SSO path
      await dispatch(getSalesforceAccessToken(email));

      // Load all master data for impersonated user
      await dispatch(fetchAllMasters(email));

      // Mark impersonation in Redux (both emails)
      await dispatch(setImpersonatedEmail(email, currentEmail));

      // Log event (fire-and-forget)
      logImpersonationEvent({ loggedInUser: currentEmail, impersonatedTo: email });

      localStorage?.removeItem("pop_status");
      navigate("/");
      return true;
    } catch (err) {
      setError(err?.message || "User not found or impersonation failed.");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const stopImpersonation = async (originalUserEmail) => {
    setLoading(true);

    try {
      // Re-login as the original user — mirrors startImpersonation exactly so
      // the full auth + data initialisation flow runs for the original user.
      await dispatch(logout());
      await dispatch(clearImpersonatedEmail());
      await dispatch(getSalesforceAccessToken(originalUserEmail));
      await dispatch(fetchAllMasters(originalUserEmail));
      navigate("/");
    } catch (err) {
      setError(err?.message || "Failed to stop impersonation.");
    } finally {
      setLoading(false);
    }
  };

  return { startImpersonation, stopImpersonation, loading, error, setError };
};
